import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { performanceAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const PerformanceManagement = () => {
  const { currentUser, isPrivileged } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ employee: '', reviewPeriod: new Date().toISOString().slice(0, 7), overallRating: 3, comments: '', status: 'normal' });
  const [editReviewId, setEditReviewId] = useState(null);
  const [activeTab, setActiveTab] = useState('reviews');
  const [salesPerfData, setSalesPerfData] = useState([]);
  const [selectedSalesPerf, setSelectedSalesPerf] = useState(null);
  const [period, setPeriod] = useState('monthly');

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await performanceAPI.getAll();
      setReviews(res?.data || res || []);
    } catch (e) { setError('Failed to load reviews'); }
    finally { setLoading(false); }
  };

  const fetchSalesPerformance = async (selectedPeriod = period) => {
    try {
      setLoading(true);
      const res = await performanceAPI.getSalesPerformance({ period: selectedPeriod });
      setSalesPerfData(res?.data || []);
    } catch (e) {
      setError('Failed to load sales performance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    if (isPrivileged) {
      employeeAPI.getAll().then(r => setEmployees(r?.data || r || [])).catch(() => {});
      fetchSalesPerformance('monthly');
    }
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditReviewId(null);
    setForm({ employee: '', reviewPeriod: new Date().toISOString().slice(0, 7), overallRating: 3, comments: '', status: 'normal' });
  };

  const handleEditStart = (r) => {
    setEditReviewId(r._id);
    setForm({
      employee: r.employee?._id || r.employee || '',
      reviewPeriod: r.reviewPeriod ? new Date(r.reviewPeriod).toISOString().slice(0, 7) : new Date().toISOString().slice(0, 7),
      overallRating: r.overallRating || 3,
      comments: r.comments || '',
      status: r.status || 'normal'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this performance review?')) {
      try {
        await performanceAPI.delete(id);
        setError('');
        fetchReviews();
      } catch (err) {
        setError(err?.message || err?.error || (typeof err === 'string' ? err : 'Failed to delete review'));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editReviewId) {
        await performanceAPI.update(editReviewId, form);
      } else {
        await performanceAPI.create(form);
      }
      handleCloseModal();
      setError('');
      fetchReviews();
    } catch (err) {
      setError(err?.message || err?.error || (typeof err === 'string' ? err : 'Failed to save review'));
    }
  };

  const renderStars = (rating) => [...Array(5)].map((_, i) => (
    <i key={i} className={i < rating ? 'fas fa-star' : 'far fa-star'} style={{ color: '#fbbf24', fontSize: 14 }}></i>
  ));

  return (
    <div className="performance-management">
      <div className="module-header">
        <h2>Performance Management</h2>
        {isPrivileged && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Add Review
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {isPrivileged && (
        <div className="tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button 
            className={`btn ${activeTab === 'reviews' ? 'btn-primary' : 'btn-secondary'}`} 
            onClick={() => setActiveTab('reviews')}
          >
            <i className="fas fa-file-alt"></i> Performance Reviews
          </button>
          <button 
            className={`btn ${activeTab === 'sales' ? 'btn-primary' : 'btn-secondary'}`} 
            onClick={() => {
              setActiveTab('sales');
              fetchSalesPerformance();
            }}
          >
            <i className="fas fa-chart-line"></i> Sales Performance Metrics
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i><p>Loading...</p></div>
      ) : activeTab === 'reviews' ? (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {isPrivileged && <th>Employee</th>}
                <th>Review Period</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Comments</th>
                <th>Reviewer</th>
                {isPrivileged && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0
                ? <tr><td colSpan="7" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No performance reviews found</td></tr>
                : reviews.map(r => (
                  <tr key={r._id}>
                    {isPrivileged && <td>{r.employee?.name || '—'}</td>}
                    <td>{r.reviewPeriod ? new Date(r.reviewPeriod).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}</td>
                    <td><div style={{ display: 'flex', gap: 2 }}>{renderStars(r.overallRating || 0)}</div></td>
                    <td>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                        background: r.status === 'promotion' ? '#d1fae5' : r.status === 'warning' ? '#fee2e2' : '#f3f4f6',
                        color: r.status === 'promotion' ? '#065f46' : r.status === 'warning' ? '#991b1b' : '#374151'
                      }}>{r.status || 'normal'}</span>
                    </td>
                    <td style={{ maxWidth: 200, fontSize: 13, color: '#6b7280' }}>{r.comments || '—'}</td>
                    <td>{r.reviewedBy?.name || '—'}</td>
                    {isPrivileged && (
                      <td>
                        <button className="btn-icon edit" title="Edit" onClick={() => handleEditStart(r)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn-icon delete" title="Delete" onClick={() => handleDelete(r._id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="filter-bar" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Period:</label>
            <select 
              value={period} 
              onChange={(e) => {
                const newPeriod = e.target.value;
                setPeriod(newPeriod);
                fetchSalesPerformance(newPeriod);
              }}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                background: '#fff',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="today">Today</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Employee ID</th>
                  <th>Department / Designation</th>
                  <th>Calls</th>
                  <th>Converted</th>
                  <th>Switch Off</th>
                  <th>Not Pickup</th>
                  <th>Not Interested</th>
                  <th>Conversion Rate</th>
                  <th>Performance Score</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {salesPerfData.length === 0 ? (
                  <tr><td colSpan="11" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No sales performance data found</td></tr>
                ) : (
                  salesPerfData.map(perf => (
                    <tr key={perf.employeeId}>
                      <td><strong>{perf.name}</strong></td>
                      <td>{perf.empIdString || '—'}</td>
                      <td>{perf.department} / {perf.designation}</td>
                      <td>
                        <span className="badge" style={{ background: '#eff6ff', color: '#1d4ed8', padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                          📞 {perf.calls || 0}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-success" style={{ background: '#d1fae5', color: '#065f46', padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                          🏆 {perf.convertedLeads}
                        </span>
                      </td>
                      <td>
                        <span className="badge" style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                          📵 {perf.switchOff || 0}
                        </span>
                      </td>
                      <td>
                        <span className="badge" style={{ background: '#f3f4f6', color: '#374151', padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                          🔇 {perf.notPickup || 0}
                        </span>
                      </td>
                      <td>
                        <span className="badge" style={{ background: '#fef3c7', color: '#92400e', padding: '4px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                          👎 {perf.notInterested || 0}
                        </span>
                      </td>
                      <td><strong>{perf.conversionRate}</strong></td>
                      <td>
                        <div style={{ display: 'flex', gap: 2 }}>
                          {renderStars(perf.performanceScore || 0)}
                        </div>
                      </td>
                      <td>
                        <button 
                          className="btn-icon view" 
                          title="View Detailed Activity" 
                          onClick={() => setSelectedSalesPerf(perf)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {isPrivileged && (
        <Modal show={showModal} onClose={handleCloseModal} title={editReviewId ? "Edit Performance Review" : "Add Performance Review"}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Employee</label>
              <select className="form-input" value={form.employee} onChange={e => setForm({ ...form, employee: e.target.value })} required disabled={editReviewId !== null}>
                <option value="">Select Employee</option>
                {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Review Period</label>
              <input type="month" className="form-input" value={form.reviewPeriod} onChange={e => setForm({ ...form, reviewPeriod: e.target.value })} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Overall Rating</label>
                <select className="form-input" value={form.overallRating} onChange={e => setForm({ ...form, overallRating: parseInt(e.target.value) })} required>
                  <option value="5">5 - Excellent</option>
                  <option value="4">4 - Very Good</option>
                  <option value="3">3 - Good</option>
                  <option value="2">2 - Average</option>
                  <option value="1">1 - Poor</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="normal">Normal</option>
                  <option value="promotion">Promotion</option>
                  <option value="warning">Warning</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Comments</label>
              <textarea className="form-input" rows="3" placeholder="Review comments..." value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} required></textarea>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
              <button type="submit" className="btn btn-primary">{editReviewId ? "Save Changes" : "Submit Review"}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Detailed Sales Performance Stats Modal */}
      <Modal 
        show={selectedSalesPerf !== null} 
        onClose={() => setSelectedSalesPerf(null)} 
        title={`Sales Performance Details — ${selectedSalesPerf?.name || ''}`}
      >
        {selectedSalesPerf && (
          <div className="employee-details" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Header info */}
            <div className="detail-section" style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', color: '#111827' }}>{selectedSalesPerf.name}</h3>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    ID: {selectedSalesPerf.empIdString || '—'} | {selectedSalesPerf.designation} ({selectedSalesPerf.department})
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', display: 'block' }}>Conversion Rating</span>
                  <div style={{ display: 'flex', gap: 2 }}>{renderStars(selectedSalesPerf.performanceScore || 0)}</div>
                </div>
              </div>
            </div>

            {/* Communication stats */}
            <div className="detail-section">
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#374151', borderBottom: '2px solid #3b82f6', display: 'inline-block', paddingBottom: '2px' }}>
                <i className="fas fa-phone-alt"></i> Communication Activity Logged
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#1e40af', fontWeight: 600 }}>📞 Calls</span>
                  <p style={{ margin: '5px 0 0 0', fontSize: '22px', fontWeight: 700, color: '#1d4ed8' }}>
                    {selectedSalesPerf.calls || 0}
                  </p>
                </div>
                <div style={{ background: '#ecfdf5', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#065f46', fontWeight: 600 }}>💬 WhatsApp</span>
                  <p style={{ margin: '5px 0 0 0', fontSize: '22px', fontWeight: 700, color: '#047857' }}>
                    {selectedSalesPerf.commBreakdown?.WhatsApp || 0}
                  </p>
                </div>
                <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#92400e', fontWeight: 600 }}>✉️ Email & SMS</span>
                  <p style={{ margin: '5px 0 0 0', fontSize: '22px', fontWeight: 700, color: '#b45309' }}>
                    {(selectedSalesPerf.commBreakdown?.Email || 0) + (selectedSalesPerf.commBreakdown?.SMS || 0)}
                  </p>
                </div>
              </div>
            </div>

            {/* Lead status breakdown */}
            <div className="detail-section">
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#374151', borderBottom: '2px solid #10b981', display: 'inline-block', paddingBottom: '2px' }}>
                <i className="fas fa-filter"></i> Lead Status Breakdown
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#d1fae5', borderRadius: '6px' }}>
                  <span style={{ color: '#065f46', fontWeight: 600 }}>🏆 Converted</span>
                  <strong style={{ color: '#047857' }}>{selectedSalesPerf.statusBreakdown?.Converted || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#fef3c7', borderRadius: '6px' }}>
                  <span style={{ color: '#92400e', fontWeight: 600 }}>📅 Follow-up</span>
                  <strong style={{ color: '#b45309' }}>{selectedSalesPerf.statusBreakdown?.['Follow-up'] || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#e0f2fe', borderRadius: '6px' }}>
                  <span style={{ color: '#075985', fontWeight: 600 }}>💼 Meeting</span>
                  <strong style={{ color: '#0369a1' }}>{selectedSalesPerf.statusBreakdown?.Meeting || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#fee2e2', borderRadius: '6px' }}>
                  <span style={{ color: '#991b1b', fontWeight: 600 }}>🔥 Hot Leads</span>
                  <strong style={{ color: '#b91c1c' }}>{selectedSalesPerf.statusBreakdown?.Hot || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#f3f4f6', borderRadius: '6px' }}>
                  <span style={{ color: '#374151', fontWeight: 500 }}>🆕 New</span>
                  <strong>{selectedSalesPerf.statusBreakdown?.New || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#f3f4f6', borderRadius: '6px' }}>
                  <span style={{ color: '#374151', fontWeight: 500 }}>❄️ Cold</span>
                  <strong>{selectedSalesPerf.statusBreakdown?.Cold || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#f3f4f6', borderRadius: '6px' }}>
                  <span style={{ color: '#6b7280', fontWeight: 500 }}>❌ Lost / Dead</span>
                  <strong>{(selectedSalesPerf.statusBreakdown?.Lost || 0) + (selectedSalesPerf.statusBreakdown?.Dead || 0)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#fee2e2', borderRadius: '6px' }}>
                  <span style={{ color: '#991b1b', fontWeight: 600 }}>📵 Switch Off</span>
                  <strong style={{ color: '#b91c1c' }}>{selectedSalesPerf.switchOff || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#f3f4f6', borderRadius: '6px' }}>
                  <span style={{ color: '#374151', fontWeight: 600 }}>🔇 Not Pickup</span>
                  <strong>{selectedSalesPerf.notPickup || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', background: '#fef3c7', borderRadius: '6px', gridColumn: 'span 2' }}>
                  <span style={{ color: '#92400e', fontWeight: 600 }}>👎 Not Interested</span>
                  <strong style={{ color: '#b45309' }}>{selectedSalesPerf.notInterested || 0}</strong>
                </div>
              </div>
            </div>

            {/* Recent Conversations / Notes */}
            <div className="detail-section">
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#374151', borderBottom: '2px solid #f59e0b', display: 'inline-block', paddingBottom: '2px' }}>
                <i className="fas fa-sticky-note"></i> Recent Notes & Conversations
              </h4>
              {selectedSalesPerf.recentNotes && selectedSalesPerf.recentNotes.length > 0 ? (
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}>
                  {selectedSalesPerf.recentNotes.map((note, index) => (
                    <div key={index} style={{ padding: '10px 0', borderBottom: index < selectedSalesPerf.recentNotes.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        <span>
                          <strong>{note.leadId}</strong> — {note.customerName} ({note.mobile})
                        </span>
                        <span>{new Date(note.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: '#374151', background: '#fafafa', padding: '6px 10px', borderRadius: '4px', borderLeft: '3px solid #f59e0b' }}>
                        {note.content}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No notes or conversation logs found for this employee</p>
              )}
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
              <button className="btn btn-secondary" onClick={() => setSelectedSalesPerf(null)}>Close</button>
            </div>

          </div>
        )}
      </Modal>
    </div>
  );
};

export default PerformanceManagement;
